import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingBasket, Sparkles, RefreshCw, Trash2, Plus } from 'lucide-react';
import { mergeItems } from '../utils/mergeShopping';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shopping-list`;
const opts = { withCredentials: true };

function Row({ it, onToggle, onDelete, showDinner }) {
  return (
    <li className="flex items-center gap-2 py-1.5 group" data-testid={`shop-item-${it.id}`}>
      <button onClick={() => onToggle(it)} aria-label={it.checked ? 'Ångra' : 'Klart'}
        className={`w-6 h-6 flex-shrink-0 rounded-md border-2 border-[#D9822B] flex items-center justify-center text-xs font-black transition-colors ${it.checked ? 'bg-[#D9822B] text-white' : 'bg-white'}`}
        data-testid={`shop-check-${it.id}`}>{it.checked ? '✓' : ''}</button>
      <span className={`flex-1 min-w-0 text-sm transition-opacity ${it.checked ? 'line-through opacity-40' : ''}`}>
        {it.name}{it.qty && <span className="text-xs text-[#B5672A] ml-1">· {it.qty}</span>}
        {showDinner && it.dinner && <span className="block text-[10px] opacity-50">till {it.dinner}</span>}
      </span>
      <button onClick={() => onDelete(it)} className="opacity-40 hover:opacity-100 p-1" aria-label="Ta bort" data-testid={`shop-delete-${it.id}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </li>
  );
}

function Groups({ items, mode, aisles, ...h }) {
  const keys = mode === 'aisle' ? aisles : [...new Set(items.map((i) => i.dinner || 'Eget'))];
  const groupOf = (i) => (mode === 'aisle' ? i.aisle : i.dinner || 'Eget');
  const shown = mode === 'aisle' ? mergeItems(items) : items.map((i) => ({ ...i, ids: [i.id] }));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {keys.map((k) => {
        const list = shown.filter((i) => groupOf(i) === k).sort((a, b) => a.checked - b.checked);
        if (!list.length) return null;
        return (
          <div key={k} className="bg-white rounded-xl border border-[#F2C9A0] px-3 py-2" data-testid={`shop-group-${k}`}>
            <p className="text-[11px] font-black uppercase tracking-wider text-[#B5672A]">{k} <span className="opacity-60">{list.filter((i) => i.checked).length}/{list.length}</span></p>
            <ul>{list.map((it) => <Row key={it.id} it={it} showDinner={mode === 'aisle'} {...h} />)}</ul>
          </div>
        );
      })}
    </div>
  );
}

export default function ShoppingList({ monday, dinnersKey }) {
  const [d, setD] = useState(null);
  const [mode, setMode] = useState('aisle');
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState('');

  const load = useCallback(() => axios.get(`${API}/${monday}`, opts).then((r) => setD(r.data)).catch(() => {}), [monday]);
  useEffect(() => { setD(null); load(); }, [load]);
  useEffect(() => { const t = setTimeout(load, 900); return () => clearTimeout(t); }, [load, dinnersKey]);

  const generate = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/${monday}/generate`, {}, { ...opts, timeout: 90000 }); setD(r.data); toast.success('Elsa har skrivit listan 🧺'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Elsa hann inte skriva listan'); }
    setBusy(false);
  };
  const setItems = (fn) => setD((c) => ({ ...c, items: fn(c.items) }));
  const onToggle = async (it) => {
    const ids = it.ids || [it.id];
    setItems((xs) => xs.map((x) => (ids.includes(x.id) ? { ...x, checked: !it.checked } : x)));
    try { await Promise.all(ids.map((id) => axios.patch(`${API}/${monday}/items/${id}`, { checked: !it.checked }, opts))); }
    catch { toast.error('Kunde inte spara'); load(); }
  };
  const onDelete = async (it) => {
    const ids = it.ids || [it.id];
    setItems((xs) => xs.filter((x) => !ids.includes(x.id)));
    try { await Promise.all(ids.map((id) => axios.delete(`${API}/${monday}/items/${id}`, opts))); } catch { load(); }
  };
  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { const r = await axios.post(`${API}/${monday}/items`, { name: text.trim() }, opts); setItems((xs) => [...xs, r.data]); setText(''); }
    catch { toast.error('Kunde inte lägga till'); }
  };
  const clearChecked = async () => {
    setItems((xs) => xs.filter((x) => !x.checked));
    try { await axios.post(`${API}/${monday}/clear-checked`, {}, opts); } catch { toast.error('Kunde inte rensa'); load(); }
  };

  if (!d) return null;
  const items = d.items || [];
  const left = (mode === 'aisle' ? mergeItems(items) : items).filter((i) => !i.checked).length;
  return (
    <section className="mb-4 rounded-xl border-2 border-[#F2C9A0] bg-[#FFFAF3] p-3" data-testid="shopping-list">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2 text-[#8A4B1E]">
          <ShoppingBasket className="w-4 h-4" />
          <span className="font-black tracking-wide">Inköpslista</span>
          {items.length > 0 && <span className="text-xs font-bold opacity-70" data-testid="shop-left">{left ? `${left} kvar` : 'Allt i korgen! 🎉'}</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {items.length > 0 && ['aisle', 'dinner'].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`shop-mode-${m}`}
              className={`px-2.5 py-1 rounded-full text-xs font-bold border-2 border-[#F2C9A0] transition-colors ${mode === m ? 'bg-[#D9822B] text-white border-[#D9822B]' : 'bg-white text-[#8A4B1E]'}`}>
              {m === 'aisle' ? 'Avdelning' : 'Per middag'}</button>
          ))}
          <button onClick={generate} disabled={busy || !d.dinners.length} data-testid="shop-generate-btn"
            className="px-3 py-1 rounded-full text-xs font-black bg-[#8A4B1E] text-white flex items-center gap-1 disabled:opacity-40 transition-transform hover:scale-105">
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {busy ? 'Elsa skriver…' : d.generated ? 'Uppdatera' : 'Elsa, gör listan'}
          </button>
        </div>
      </div>
      {d.stale && !busy && <p className="text-xs mb-2 text-[#8A4B1E] bg-[#FFE8CC] rounded-lg px-2 py-1" data-testid="shop-stale">Middagarna har ändrats. Tryck på Uppdatera så fixar Elsa listan 💛</p>}
      {!d.dinners.length && !items.length && <p className="text-xs italic text-[#8A4B1E] opacity-80" data-testid="shop-empty">Skriv några middagar ovanför, så gör Elsa en inköpslista åt dig.</p>}
      {items.length > 0 && <Groups items={items} mode={mode} aisles={d.aisles} onToggle={onToggle} onDelete={onDelete} />}
      <form onSubmit={add} className="mt-2 flex items-center gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Lägg till något mer, t.ex. mjölk" maxLength={80}
          className="flex-1 min-w-0 text-sm px-2 py-1.5 rounded-lg border border-[#F2C9A0] bg-white outline-none focus:border-[#D9822B]" data-testid="shop-add-input" />
        <button type="submit" className="p-2 rounded-lg bg-[#D9822B] text-white" aria-label="Lägg till" data-testid="shop-add-btn"><Plus className="w-4 h-4" /></button>
        {items.some((i) => i.checked) && <button type="button" onClick={clearChecked} className="text-xs font-bold text-[#8A4B1E] underline whitespace-nowrap" data-testid="shop-clear-checked">Rensa avbockade</button>}
      </form>
    </section>
  );
}
